/**
 * Public Telegram channel follow URLs for discovery (Phase 6).
 * Client-safe — uses NEXT_PUBLIC_* only (not bot token).
 */

export type TelegramPublicChannelKey = "lagos" | "oyo";

export type TelegramPublicChannelLink = {
  key: TelegramPublicChannelKey;
  /** Button / link label (plain language). */
  label: string;
  url: string;
};

const DEFAULT_LAGOS_URL = "https://t.me/SellItOffLagos";
const DEFAULT_OYO_URL = "https://t.me/SellItOffOyo";

/**
 * Accepts full https://t.me/… URL, @username, or bare username.
 * Numeric -100… chat ids are not public follow links — fall back to default.
 */
export function normalizeTelegramPublicChannelUrl(
  raw: string | null | undefined,
  fallback: string,
): string {
  const trimmed = raw?.trim();
  if (!trimmed) return fallback;

  if (/^https?:\/\/(t\.me|telegram\.me)\//i.test(trimmed)) {
    return trimmed.replace(/\/+$/, "");
  }

  if (trimmed.startsWith("@")) {
    const user = trimmed.slice(1);
    return user ? `https://t.me/${user}` : fallback;
  }

  if (/^[A-Za-z][A-Za-z0-9_]{3,}$/.test(trimmed)) {
    return `https://t.me/${trimmed}`;
  }

  return fallback;
}

/**
 * Lagos + Oyo public channel links for “Follow listings on Telegram”.
 */
export function getTelegramListingPublicChannelLinks(): TelegramPublicChannelLink[] {
  const lagosUrl = normalizeTelegramPublicChannelUrl(
    process.env.NEXT_PUBLIC_TELEGRAM_CHANNEL_LAGOS_URL ??
      process.env.NEXT_PUBLIC_TELEGRAM_CHANNEL_LAGOS_CHAT_ID,
    DEFAULT_LAGOS_URL,
  );
  const oyoUrl = normalizeTelegramPublicChannelUrl(
    process.env.NEXT_PUBLIC_TELEGRAM_CHANNEL_OYO_URL ??
      process.env.NEXT_PUBLIC_TELEGRAM_CHANNEL_OYO_CHAT_ID,
    DEFAULT_OYO_URL,
  );

  return [
    { key: "lagos", label: "Lagos channel", url: lagosUrl },
    { key: "oyo", label: "Oyo channel", url: oyoUrl },
  ];
}

export const TELEGRAM_LISTING_FOLLOW_HEADING = "Follow listings on Telegram";

export const TELEGRAM_LISTING_FOLLOW_DESCRIPTION =
  "Public Lagos and Oyo listing feeds you can browse anytime. Separate from near-me alerts (those message you about items near you).";
